import type { VisualTheme } from "../lib/themeLoader";

/** Inline-styled mockup of a theme's surface treatment. Renders a small
 *  swatch + two miniature cards in the target theme's signature look so
 *  the user can preview each theme without globally swapping them.
 *
 *  Used by Settings → Display tile grid AND the Header → Palette
 *  overlay. Same component, same visual contract - keeps both surfaces
 *  in sync as new themes get added.
 *
 *  The original 6 themes (flat / glass-v1 / glass-v2 / glass-v3 /
 *  neumorphism / neon) get hand-painted previews because each has a
 *  distinct structural signature. The 12 themes added in v0.13.0 fall
 *  through to a `palette` lookup table that schematically renders the
 *  right border-radius / stroke style / shadow flavour. The full
 *  structural difference shows up on the live page once the user taps. */
export function ThemeMiniature({ themeId }: { themeId: VisualTheme }) {
  const wrapperBase =
    "relative w-full h-16 rounded-lg overflow-hidden flex items-center gap-1.5 px-2";
  switch (themeId) {
    case "flat":
      return (
        <div className={wrapperBase} style={{ background: "#f1f5f9" }}>
          <div
            className="w-7 h-9 rounded-md"
            style={{
              background: "white",
              border: "1px solid #cbd5e1",
              boxShadow: "0 2px 6px rgba(15,23,42,0.08)",
            }}
          />
          <div
            className="w-12 h-9 rounded-md"
            style={{
              background: "white",
              border: "1px solid #cbd5e1",
              boxShadow: "0 2px 6px rgba(15,23,42,0.08)",
            }}
          />
        </div>
      );
    case "glass-v1":
      return (
        <div
          className={wrapperBase}
          style={{ background: "linear-gradient(135deg, #e8eef5, #eef2f8)" }}
        >
          <div
            className="w-7 h-9 rounded-md"
            style={{
              background: "rgba(255,255,255,0.65)",
              border: "1px solid rgba(255,255,255,0.5)",
              backdropFilter: "blur(6px)",
              boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
            }}
          />
          <div
            className="w-12 h-9 rounded-md"
            style={{
              background: "rgba(255,255,255,0.78)",
              border: "1px solid rgba(255,255,255,0.6)",
              boxShadow: "0 4px 12px rgba(15,23,42,0.1)",
            }}
          />
        </div>
      );
    case "glass-v2":
      return (
        <div
          className={wrapperBase}
          style={{
            background:
              "linear-gradient(135deg, #e8eef5 0%, #f0f4f9 50%, #eef2f8 100%)",
          }}
        >
          <div
            className="w-7 h-9 rounded-md relative"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.65) 50%)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
            }}
          />
          <div
            className="w-12 h-9 rounded-md relative"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.78) 50%)",
              border: "1px solid rgba(255,255,255,0.6)",
              boxShadow: "0 4px 12px rgba(15,23,42,0.1)",
            }}
          />
        </div>
      );
    case "glass-v3":
      return (
        <div
          className={wrapperBase}
          style={{
            background:
              "radial-gradient(ellipse at 20% 30%, rgba(168,85,247,0.35), transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(34,211,238,0.35), transparent 50%), linear-gradient(135deg, #f4ecff, #ecf6ff)",
          }}
        >
          <div
            className="w-7 h-9 rounded-md"
            style={{
              background: "rgba(168,85,247,0.12)",
              border: "1px solid rgba(168,85,247,0.4)",
              boxShadow: "0 4px 14px rgba(76,29,149,0.18)",
            }}
          />
          <div
            className="w-12 h-9 rounded-md"
            style={{
              background: "rgba(255,255,255,0.5)",
              border: "1px solid rgba(34,211,238,0.4)",
              boxShadow: "0 4px 14px rgba(76,29,149,0.18)",
            }}
          />
        </div>
      );
    case "glass-extreme":
      return (
        <div
          className={wrapperBase}
          style={{
            // iOS-wallpaper-style multi-stop gradient mini. The
            // miniature can't actually run a 40px backdrop-filter at
            // this size (would smear nothing), so we approximate the
            // look with very translucent rgba cards + a soft inset
            // hairline that mirrors the box-shadow trick the live
            // theme uses.
            background:
              "linear-gradient(135deg, #c7d2fe 0%, #ddd6fe 30%, #fbcfe8 60%, #c4b5fd 100%)",
          }}
        >
          <div
            className="w-7 h-9"
            style={{
              background: "rgba(255,255,255,0.45)",
              borderRadius: "10px",
              boxShadow:
                "inset 0 0 0 0.5px rgba(255,255,255,0.7), 0 4px 12px rgba(15,23,42,0.08)",
              backdropFilter: "blur(8px) saturate(180%)",
            }}
          />
          <div
            className="w-12 h-9"
            style={{
              background: "rgba(255,255,255,0.55)",
              borderRadius: "10px",
              boxShadow:
                "inset 0 0 0 0.5px rgba(255,255,255,0.8), 0 6px 14px rgba(15,23,42,0.1)",
              backdropFilter: "blur(10px) saturate(200%)",
            }}
          />
        </div>
      );
    case "frosted-glass":
      return (
        <div
          className={wrapperBase}
          style={{
            // Teal/cyan gradient backdrop with translucent cards on top.
            // The miniature can't actually render a 30px backdrop-filter
            // (would smear nothing at this size), so we approximate with
            // teal-tinted rgba cards + a soft glint to telegraph the
            // ocean palette + heavy-frost personality.
            background:
              "linear-gradient(135deg, #cffafe 0%, #ecfdf5 50%, #cffafe 100%)",
          }}
        >
          <div
            className="w-7 h-9 rounded-md"
            style={{
              background: "rgba(240,252,252,0.55)",
              border: "1px solid rgba(0,200,200,0.22)",
              boxShadow: "0 4px 14px rgba(0,100,100,0.20)",
              backdropFilter: "blur(8px) saturate(180%)",
            }}
          />
          <div
            className="w-12 h-9 rounded-md"
            style={{
              background: "rgba(245,254,254,0.72)",
              border: "1px solid rgba(0,200,200,0.25)",
              boxShadow: "0 6px 18px rgba(0,100,100,0.24)",
              backdropFilter: "blur(10px) saturate(180%)",
            }}
          />
        </div>
      );
    case "neumorphism":
      return (
        <div className={wrapperBase} style={{ background: "#e0e5ec" }}>
          <div
            className="w-7 h-9 rounded-md"
            style={{
              background: "#e0e5ec",
              boxShadow:
                "-3px -3px 6px rgba(255,255,255,0.85), 3px 3px 6px rgba(174,174,192,0.45)",
            }}
          />
          <div
            className="w-12 h-9 rounded-md"
            style={{
              background: "#e0e5ec",
              boxShadow:
                "-4px -4px 8px rgba(255,255,255,0.9), 4px 4px 8px rgba(174,174,192,0.5)",
            }}
          />
        </div>
      );
    case "neon":
      return (
        <div
          className={wrapperBase}
          style={{ background: "linear-gradient(180deg, #050510, #0c0c1a)" }}
        >
          <div
            className="w-7 h-9 rounded-md"
            style={{
              background: "rgba(5,10,20,0.78)",
              border: "1px solid #00ffff",
              boxShadow:
                "0 0 8px rgba(0,255,255,0.55), inset 0 0 6px rgba(0,255,255,0.1)",
            }}
          />
          <div
            className="w-12 h-9 rounded-md"
            style={{
              background: "rgba(8,4,16,0.82)",
              border: "1px solid #ff00ff",
              boxShadow:
                "0 0 8px rgba(255,0,255,0.55), inset 0 0 6px rgba(255,0,255,0.1)",
            }}
          />
        </div>
      );
    default: {
      const palette: Record<
        string,
        { bg: string; cardBg: string; cardBorder: string; radius?: string }
      > = {
        ocean: {
          bg: "linear-gradient(135deg,#cffafe,#a7f3d0)",
          cardBg: "rgba(165,243,252,0.5)",
          cardBorder: "rgba(34,211,238,0.5)",
        },
        sunset: {
          bg: "linear-gradient(180deg,#fff7ed,#fce7f3,#ede9fe)",
          cardBg: "linear-gradient(165deg,#fed7aa,#fbcfe8,#ddd6fe)",
          cardBorder: "transparent",
          radius: "10px",
        },
        forest: {
          bg: "linear-gradient(180deg,#fef9c3,#ecfccb)",
          cardBg: "#fefce8",
          cardBorder: "rgba(120,53,15,0.3)",
          radius: "10px 8px 11px 9px",
        },
        arctic: {
          bg: "#f8fafc",
          cardBg: "#f8fafc",
          cardBorder: "#cbd5e1",
          radius: "4px",
        },
        lavender: {
          bg: "radial-gradient(ellipse at 20% 20%,rgba(196,181,253,0.6),transparent 60%),linear-gradient(135deg,#faf5ff,#fce7f3)",
          cardBg:
            "radial-gradient(ellipse at 30% 30%,rgba(196,181,253,0.5),transparent 60%),rgba(250,245,255,0.85)",
          cardBorder: "rgba(167,139,250,0.4)",
          radius: "10px",
        },
        "rose-gold": {
          bg: "linear-gradient(135deg,#fff7ed,#fef2f2)",
          cardBg:
            "linear-gradient(180deg,#fef3c7,#fde2cf,#f5d0c5,#fde2cf,#fef3c7)",
          cardBorder: "rgba(180,83,9,0.4)",
          radius: "6px",
        },
        midnight: {
          bg: "linear-gradient(180deg,#020617,#0a0e1c)",
          cardBg: "rgba(2,6,23,0.7)",
          cardBorder: "transparent",
          radius: "6px",
        },
        copper: {
          bg: "#f5e6d3",
          cardBg: "#f5e6d3",
          cardBorder: "rgba(180,83,9,0.3)",
          radius: "5px",
        },
        emerald: {
          bg: "linear-gradient(180deg,#d1fae5,#a7f3d0)",
          cardBg: "white",
          cardBorder: "transparent",
          radius: "16px",
        },
        sakura: {
          bg: "linear-gradient(180deg,#fff,#fdf2f8)",
          cardBg: "rgba(255,255,255,0.85)",
          cardBorder: "rgba(244,114,182,0.6)",
          radius: "6px",
        },
        "slate-modern": {
          bg: "linear-gradient(180deg,#fff,#f1f5f9)",
          cardBg: "#ffffff",
          cardBorder: "#0f172a",
          radius: "0",
        },
        nord: {
          bg: "linear-gradient(180deg,#eceff4,#e5e9f0)",
          cardBg: "white",
          cardBorder: "#d8dee9",
          radius: "5px",
        },
      };
      const p = palette[themeId] ?? {
        bg: "#f1f5f9",
        cardBg: "white",
        cardBorder: "#cbd5e1",
      };
      const stroke =
        themeId === "slate-modern"
          ? `2px solid ${p.cardBorder}`
          : themeId === "sakura"
            ? `1.5px dashed ${p.cardBorder}`
            : `1px solid ${p.cardBorder}`;
      const shadow =
        themeId === "slate-modern"
          ? "3px 3px 0 #0f172a"
          : themeId === "emerald"
            ? "0 8px 16px -4px rgba(6,78,59,0.25)"
            : themeId === "midnight"
              ? "inset 0 1px 0 rgba(125,211,252,0.9), 0 0 8px rgba(56,189,248,0.5)"
              : themeId === "copper"
                ? "-2px -2px 4px rgba(255,245,230,0.85), 2px 2px 6px rgba(120,53,15,0.25)"
                : "0 2px 6px rgba(15,23,42,0.1)";
      return (
        <div className={wrapperBase} style={{ background: p.bg }}>
          <div
            className="w-7 h-9"
            style={{
              background: p.cardBg,
              border: stroke,
              borderRadius: p.radius ?? "8px",
              boxShadow: shadow,
            }}
          />
          <div
            className="w-12 h-9"
            style={{
              background: p.cardBg,
              border: stroke,
              borderRadius: p.radius ?? "8px",
              boxShadow: shadow,
            }}
          />
        </div>
      );
    }
  }
}
